import * as amplitude from '@amplitude/analytics-browser'
import constants from '../constants/constant'

type AnyRecord = Record<string, any>

const AMPLITUDE_API_KEY = process.env.REACT_APP_AMPLITUDE_API_KEY || ''
// Same key CurrentClientProvider persists the active client under.
const ACTIVE_CLIENT_STORAGE_KEY = 'retailorbit.active_client'
const SPOOF_ORIGIN_USER_STORAGE_KEY = 'spoof_origin_user'
const MANUAL_UPLOAD_POS_IDS = new Set([1112, 1114])
const SHOPIFY_POS_IDS = new Set([56, 1171, 1172, 1214])
const LIGHTSPEED_X_POS_IDS = new Set([63, 1201])
const LIGHTSPEED_R_POS_IDS = new Set([27, 1113, 1177, 1178, 1188, 1212])
const HEARTLAND_POS_IDS = new Set([1202])
const RUNIT_POS_IDS = new Set([47, 1139, 1187, 1204])

function readString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function firstString(...values: unknown[]) {
  for (const value of values) {
    const resolved = readString(value)
    if (resolved) return resolved
  }
  return null
}

function readScalarString(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }

  return readString(value)
}

function firstScalarString(...values: unknown[]) {
  for (const value of values) {
    const resolved = readScalarString(value)
    if (resolved) return resolved
  }
  return null
}

function normalizeAmplitudeId(prefix: string, value: unknown) {
  const rawValue = firstString(value, typeof value === 'number' ? String(value) : null)
  if (!rawValue) return null

  const normalizedValue = rawValue.startsWith(`${prefix}_`) ? rawValue : `${prefix}_${rawValue}`
  return normalizedValue.length >= 5 ? normalizedValue : null
}

function resolveLegacyRole(user: AnyRecord | null | undefined) {
  const userRoleTags = user?.roleTags
  const userRoles = user?.roles
  const roleTags: string[] = Array.isArray(userRoleTags) ? userRoleTags.map((tag) => String(tag)) : []
  const roles: AnyRecord[] = Array.isArray(userRoles) ? userRoles : []
  const isRetailer = roleTags.includes(String(constants.ROLE_ID.client))
    || roles.some((role) => String(role?.id) === String(constants.ROLE_ID.client) || String(role?.name || '').toLowerCase() === 'client')

  return isRetailer ? 'Retailer' : 'Consultant'
}

// Prefer the server-computed analytics_role (Internal / Consultant / Retailer).
// Fall back to the legacy client-side guess only while it is missing, e.g. for
// locally cached users that pre-date the server-side role.
function resolveRole(user: AnyRecord | null | undefined) {
  return readString(user?.analytics_role) || resolveLegacyRole(user)
}

function getSpoofOriginUser(): AnyRecord | null {
  if (typeof window === 'undefined') return null

  try {
    const rawUser = window.localStorage.getItem(SPOOF_ORIGIN_USER_STORAGE_KEY)
    return rawUser ? JSON.parse(rawUser) : null
  } catch (error) {
    console.warn('Unable to read spoof origin user from local storage:', error)
    return null
  }
}

function readStoredActiveClient(): AnyRecord | null {
  if (typeof window === 'undefined') return null

  try {
    const rawClient = window.localStorage.getItem(ACTIVE_CLIENT_STORAGE_KEY)
    return rawClient ? JSON.parse(rawClient) : null
  } catch (error) {
    console.warn('Unable to read active client from local storage:', error)
    return null
  }
}

// In-memory snapshot of what was last sent to amplitude.identify(), so the
// orbit-context enrichment plugin can stamp the same role/impersonation info
// on every event (including autocaptured clicks) without re-deriving it.
let lastAmplitudeRole: string | null = null
let lastAmplitudeImpersonation: { impersonated_user_id: string | null; impersonated_client_code: string | null } | null = null

export function clearAmplitudeImpersonation() {
  lastAmplitudeImpersonation = null
}

export function getAmplitudeContext(): AnyRecord {
  const context: AnyRecord = {}
  const storedClient = readStoredActiveClient()
  const clientCode = resolveClientCode(storedClient)
  const clientId = resolveClientId(storedClient)

  if (clientCode) context.client_code = clientCode
  if (clientId) context.client_id = clientId
  if (lastAmplitudeRole) context.user_role = lastAmplitudeRole

  if (lastAmplitudeImpersonation?.impersonated_user_id) {
    context.impersonated_user_id = lastAmplitudeImpersonation.impersonated_user_id
    if (lastAmplitudeImpersonation.impersonated_client_code) {
      context.impersonated_client_code = lastAmplitudeImpersonation.impersonated_client_code
    }
  }

  return context
}

function resolvePosSystemFromName(name: string | null) {
  const normalizedName = String(name || '').toLowerCase()

  if (!normalizedName) return 'Manual Upload'
  if (normalizedName.includes('shopify')) return 'Shopify'
  if (normalizedName.includes('lightspeedx') || normalizedName.includes('lightspeed x') || normalizedName.includes('vend')) return 'Lightspeed X'
  if (normalizedName.includes('lightspeed')) return 'Lightspeed R'
  if (normalizedName.includes('heartland')) return 'Heartland'
  if (normalizedName.includes('runit')) return 'RunIt'
  if (normalizedName.includes('netsuite')) return 'NetSuite'
  if (normalizedName.includes('manual upload') || normalizedName.includes('unknown') || normalizedName.includes('skuupload')) return 'Manual Upload'

  return name || 'Manual Upload'
}

function resolvePosSystem(client: AnyRecord | null | undefined) {
  const pointOfSaleId = Number(client?.PointOfSaleId ?? client?.pointOfSaleId ?? client?.point_of_sale_id)
  if (Number.isFinite(pointOfSaleId)) {
    if (SHOPIFY_POS_IDS.has(pointOfSaleId)) return 'Shopify'
    if (LIGHTSPEED_X_POS_IDS.has(pointOfSaleId)) return 'Lightspeed X'
    if (LIGHTSPEED_R_POS_IDS.has(pointOfSaleId)) return 'Lightspeed R'
    if (HEARTLAND_POS_IDS.has(pointOfSaleId)) return 'Heartland'
    if (RUNIT_POS_IDS.has(pointOfSaleId)) return 'RunIt'
    if (MANUAL_UPLOAD_POS_IDS.has(pointOfSaleId)) return 'Manual Upload'
  }

  return resolvePosSystemFromName(firstString(
    client?.PointOfSaleName,
    client?.pointOfSaleName,
    client?.point_of_sale_name,
    client?.pointOfSale?.name,
    client?.pointOfSale?.Name,
    client?.point_of_sale?.name,
    client?.point_of_sale?.Name,
    client?.shopifyClient ? 'Shopify' : null,
  ))
}

export function resolvePosSystemLabelForTest(client: AnyRecord | null | undefined) {
  return resolvePosSystem(client)
}

function resolvePlanTier(client: AnyRecord | null | undefined) {
  return firstString(
    client?.plan_tier,
    client?.planTier,
    client?.PlanTier,
    client?.clientExtra?.plan_tier,
    client?.clientExtra?.planTier,
    client?.clientExtra?.tier,
    client?.client_extra?.plan_tier,
    client?.client_extra?.planTier,
    client?.client_extra?.tier,
  ) || 'Unknown'
}

function resolveVertical(client: AnyRecord | null | undefined) {
  return firstString(
    client?.vertical,
    client?.shopifyClient?.vertical,
    client?.clientExtra?.vertical,
    client?.clientExtra?.verticals,
    client?.client_extra?.vertical,
    client?.client_extra?.verticals,
  ) || 'Unknown'
}

function resolveClientName(client: AnyRecord | null | undefined) {
  return firstString(client?.Name, client?.name, client?.ClientName, client?.client_name) || 'Unknown'
}

function resolveClientCode(client: AnyRecord | null | undefined) {
  return firstString(client?.ClientCode, client?.clientCode, client?.client_code)
}

function resolveClientId(client: AnyRecord | null | undefined) {
  return firstScalarString(client?.Id, client?.id, client?.client_id)
}

function resolveUserEmail(user: AnyRecord | null | undefined) {
  return firstString(user?.email, user?.Email)
}

function resolveUsername(user: AnyRecord | null | undefined) {
  return firstString(user?.username, user?.user_name, user?.email, user?.Email)
}

function resolveUserFirstName(user: AnyRecord | null | undefined) {
  return firstString(user?.first_name, user?.firstName, user?.FirstName)
}

function resolveUserLastName(user: AnyRecord | null | undefined) {
  return firstString(user?.last_name, user?.lastName, user?.LastName)
}

function resolveUserFullName(user: AnyRecord | null | undefined) {
  const firstName = resolveUserFirstName(user)
  const lastName = resolveUserLastName(user)

  if (firstName && lastName) return `${firstName} ${lastName}`
  if (firstName) return firstName
  if (lastName) return lastName

  return resolveUsername(user)
}

export function getAmplitudeApiKey() {
  return AMPLITUDE_API_KEY
}

export function buildAmplitudeUserId(user: AnyRecord | null | undefined) {
  return normalizeAmplitudeId('user', user?.id ?? user?.Id ?? user?.user_id)
}

export function getStoredAmplitudeUserId() {
  if (typeof window === 'undefined') return null

  try {
    const rawUser = window.localStorage.getItem('current_user')
    if (!rawUser) return null
    return buildAmplitudeUserId(JSON.parse(rawUser))
  } catch (error) {
    console.warn('Unable to derive Amplitude user id from local storage:', error)
    return null
  }
}

export function buildAmplitudeIdentifyPayload(user: AnyRecord | null | undefined, client?: AnyRecord | null) {
  const resolvedClient = client || user?.client || null

  return {
    auth_user_id: firstScalarString(user?.id, user?.Id, user?.user_id),
    auth_username: resolveUsername(user),
    auth_email: resolveUserEmail(user),
    auth_first_name: resolveUserFirstName(user),
    auth_last_name: resolveUserLastName(user),
    auth_full_name: resolveUserFullName(user),
    client_name: resolveClientName(resolvedClient),
    selected_client_id: resolveClientId(resolvedClient),
    selected_client_code: resolveClientCode(resolvedClient),
    selected_client_name: resolveClientName(resolvedClient),
    pos_system: resolvePosSystem(resolvedClient),
    plan_tier: resolvePlanTier(resolvedClient),
    role: resolveRole(user),
    vertical: resolveVertical(resolvedClient),
  }
}

export function syncAmplitudeIdentity({ user, client }: { user: AnyRecord | null | undefined; client?: AnyRecord | null }) {
  if (!AMPLITUDE_API_KEY) return

  // While spoofing, keep the Amplitude identity on the staff member (the
  // origin user) instead of the retailer being viewed. The retailer's
  // Amplitude user must never receive setUserId/identify calls.
  const originUser = getSpoofOriginUser()
  const isSpoofing = Boolean(originUser)
  const identityUser = isSpoofing ? originUser : user
  const identityClient = isSpoofing ? (originUser?.client || null) : client

  const userId = buildAmplitudeUserId(identityUser)
  if (!userId) return

  amplitude.setOptOut(false)
  amplitude.setUserId(userId)

  const identifyEvent = new amplitude.Identify()
  const payload = buildAmplitudeIdentifyPayload(identityUser, identityClient)

  if (isSpoofing) {
    payload.role = 'Internal'
  }

  Object.entries(payload).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      identifyEvent.set(key, value)
    }
  })

  amplitude.identify(identifyEvent)

  lastAmplitudeRole = payload.role || null
  lastAmplitudeImpersonation = isSpoofing
    ? {
        impersonated_user_id: firstScalarString(user?.id, user?.Id, user?.user_id),
        impersonated_client_code: resolveClientCode(client || user?.client),
      }
    : null
}




